import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Star, Wrench, X } from 'lucide-react'
import api from '../lib/api'
import { cn } from '../lib/utils'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog'

// What's New for apps whose changelog lives only in the internal admin
// (Ping, Pocket). Releases are authored per app at /<app>/whats-new in the
// admin; GET /whats-new?app=<kind> serves that app's published releases plus
// the all-apps announcements, newest first.
//
// Frame keeps its own UpdatesModal (apps/frame) because it also carries a
// bundled static changelog; these apps have none, so the trigger is simply
// hidden until something is published — never an empty popup.

export type WhatsNewApp = 'ping' | 'pos'

type Entry = { type: string; text_en: string; text_ar: string }
type Release = {
  id: number
  version: string | null
  title_en: string | null
  title_ar: string | null
  date_label: string | null
  published_at: string | null
  entries: Entry[]
}

const seenKey = (app: WhatsNewApp) => `wt.whats-new.seen.${app}`

/** Published releases for `app`, and whether the newest is unread here. */
export function useWhatsNew(app: WhatsNewApp) {
  const [releases, setReleases] = useState<Release[]>([])
  const [unread, setUnread] = useState(false)

  useEffect(() => {
    let alive = true
    api.get('/whats-new', { params: { app } })
      .then(r => {
        if (!alive) return
        const rows: Release[] = r.data?.releases ?? []
        setReleases(rows)
        let seen = 0
        try { seen = Number(localStorage.getItem(seenKey(app)) ?? 0) } catch { /* storage blocked */ }
        setUnread(rows.length > 0 && rows[0].id > seen)
      })
      // Offline or unreachable: no button rather than a broken one.
      .catch(() => { if (alive) setReleases([]) })
    return () => { alive = false }
  }, [app])

  const markSeen = useCallback(() => {
    setUnread(false)
    if (!releases.length) return
    try { localStorage.setItem(seenKey(app), String(releases[0].id)) } catch { /* storage blocked */ }
  }, [app, releases])

  return { releases, unread, markSeen }
}

const TYPE_STYLE: Record<string, { Icon: typeof Sparkles; tile: string; icon: string; label: string }> = {
  new:     { Icon: Sparkles, tile: 'bg-primary', icon: 'text-primary-foreground', label: 'text-primary' },
  improve: { Icon: Star, tile: 'bg-emerald-100 dark:bg-emerald-950/40', icon: 'text-emerald-600 dark:text-emerald-400', label: 'text-emerald-600 dark:text-emerald-400' },
  fix:     { Icon: Wrench, tile: 'bg-amber-100 dark:bg-amber-950/40', icon: 'text-amber-600 dark:text-amber-400', label: 'text-amber-600 dark:text-amber-400' },
}

const SECTIONS = [
  { type: 'new', key: 'updates.sections.new', fallback: 'New Features' },
  { type: 'improve', key: 'updates.sections.improve', fallback: 'Improvements' },
  { type: 'fix', key: 'updates.sections.fixes', fallback: 'Bug Fixes' },
] as const

function ReleaseBlock({ release, ar }: { release: Release; ar: boolean }) {
  const { t } = useTranslation()
  const text = (e: Entry) => (ar ? e.text_ar : e.text_en) || e.text_en || e.text_ar
  const title = (ar ? release.title_ar : release.title_en) || release.title_en || release.title_ar
  const when = release.date_label
    || (release.published_at ? new Date(release.published_at).toLocaleDateString(ar ? 'ar' : undefined) : '')
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2 flex-wrap">
        {release.version && <span className="font-semibold text-foreground">v{release.version}</span>}
        {title && <span className="text-sm text-foreground/90">{title}</span>}
        {when && <span className="text-xs text-muted-foreground">{when}</span>}
      </div>
      {SECTIONS.map(({ type, key, fallback }) => {
        const items = release.entries.filter(e => (TYPE_STYLE[e.type] ? e.type : 'new') === type)
        if (!items.length) return null
        const s = TYPE_STYLE[type]
        return (
          <div key={type}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(key, fallback)}</p>
            <div className="divide-y divide-border/50 rounded-lg border bg-card px-3">
              {items.map((e, i) => (
                <div key={i} className="flex items-start gap-3 py-2">
                  <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', s.tile)}>
                    <s.Icon className={cn('h-3.5 w-3.5', s.icon)} />
                  </span>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <Badge variant="outline" className={cn('text-[10px] font-semibold uppercase tracking-wide shrink-0', s.label)}>
                      {t(`updates.types.${type}`, type)}
                    </Badge>
                    <span className="text-sm text-foreground/90">{text(e)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </section>
  )
}

export function WhatsNewModal({
  open, onOpenChange, releases,
}: { open: boolean; onOpenChange: (v: boolean) => void; releases: Release[] }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language?.startsWith('ar') ?? false
  const close = () => onOpenChange(false)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full p-0 gap-0 overflow-hidden max-h-[90vh] [&>button]:hidden">
        <div className="border-b px-4 sm:px-6 py-4">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary">
                <Sparkles className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-lg truncate">{t('updates.title', "What's New in WeTook")}</DialogTitle>
                <DialogDescription className="hidden sm:block text-sm mt-0.5 truncate">
                  {t('updates.subtitle', 'Stay up to date with the latest improvements')}
                </DialogDescription>
              </div>
              <button
                onClick={close}
                className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
                aria-label={t('common.close', 'Close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </DialogHeader>
        </div>
        <div className="no-scrollbar flex flex-col gap-6 overflow-y-auto p-4 sm:p-6" style={{ maxHeight: 'calc(90vh - 150px)' }}>
          {releases.map(r => <ReleaseBlock key={r.id} release={r} ar={ar} />)}
        </div>
        <div className="flex items-center justify-end border-t bg-muted/20 px-6 py-3">
          <Button size="sm" onClick={close}>{t('updates.gotIt', 'Got it!')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Top-bar trigger (sparkles + unread dot), matching Frame's. Renders nothing
 *  until the app has at least one published release. */
export function WhatsNewButton({ app }: { app: WhatsNewApp }) {
  const { t } = useTranslation()
  const { releases, unread, markSeen } = useWhatsNew(app)
  const [open, setOpen] = useState(false)
  if (!releases.length) return null
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => { setOpen(true); markSeen() }}
        className="relative w-8 h-8 rounded-md text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
        aria-label={t('updates.title', "What's New in WeTook")}
        title={t('updates.title', "What's New in WeTook")}
      >
        <Sparkles className="w-[14px] h-[14px]" />
        {unread && <span className="absolute top-[6px] end-[6px] w-1.5 h-1.5 bg-primary rounded-full ring-2 ring-sidebar" />}
      </Button>
      <WhatsNewModal open={open} onOpenChange={setOpen} releases={releases} />
    </>
  )
}
